import { useEffect, useState } from 'react';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listOwnMcpApiKeys, revokeOwnMcpApiKey } from '@/apps/apis/mcpAccess';
import { AGENT_ADD_MCP_COMMANDS, AGENT_INSTALL_COMMANDS } from '@/config/agentInstall';
import { errorMessage, formatWhen } from './mcpAccessFormat';

const KEYS_QUERY_KEY = ['mcpAccess', 'keys'] as const;
const STORED_LABEL_PREFIX = 'dashboard:';

const deviceName = (label: string | null): string => (label ? label.replace(STORED_LABEL_PREFIX, '') : '(no name)');

type CopyState = 'idle' | 'copied' | 'failed';

/** How long "Copied" stays on the button before it goes back to "Copy". */
const COPIED_FEEDBACK_MS = 2500;

/** One command in a box with its own copy button; a refused copy leaves the text selectable on screen. */
const CopyableCommand = ({ label, command }: { label: string; command: string }) => {
  const [copyState, setCopyState] = useState<CopyState>('idle');

  useEffect(() => {
    if (copyState !== 'copied') {
      return undefined;
    }
    const timer = setTimeout(() => setCopyState('idle'), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copyState]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <div className="mb-2">
      <div className="mb-1">{label}</div>
      <div className="d-flex align-items-start gap-2">
        <pre
          className="bg-light border rounded p-2 mb-0 flex-grow-1"
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
        >
          {command}
        </pre>
        <Button
          variant={copyState === 'copied' ? 'success' : 'outline-secondary'}
          size="sm"
          aria-label={`Copy: ${label}`}
          onClick={() => void copy()}
        >
          {copyState === 'copied' ? 'Copied ✓' : 'Copy'}
        </Button>
      </div>
      {copyState === 'failed' && (
        <div className="text-danger">Could not copy. Select the command and copy it by hand.</div>
      )}
    </div>
  );
};

/** Your own MCP keys, kept until every personal key is revoked at the SSD-7546 cutover. */
const McpAccessContent = () => {
  const queryClient = useQueryClient();
  const keys = useQuery({ queryKey: KEYS_QUERY_KEY, queryFn: listOwnMcpApiKeys });
  const [showSetup, setShowSetup] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const refreshKeys = () => queryClient.invalidateQueries({ queryKey: KEYS_QUERY_KEY });

  const revoke = async (keyId: string) => {
    if (revoking) {
      return;
    }
    setRevoking(keyId);
    setRevokeError(null);
    try {
      await revokeOwnMcpApiKey(keyId);
      setConfirming(null);
      await refreshKeys();
    } catch (caught) {
      setRevokeError(errorMessage(caught, 'Unable to revoke the key.'));
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div>
      <h3 className="mb-1">MCP access</h3>
      <Alert variant="info" className="mb-4 small">
        Make sure you have the SkinSeoul Agent Plugin installed.{' '}
        <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => setShowSetup((shown) => !shown)}>
          {showSetup ? 'Hide commands' : 'Show commands'}
        </Button>
        {showSetup && (
          <div className="mt-2">
            <div className="fw-semibold mb-1">SkinSeoul tools missing in Claude Code? Run:</div>
            <CopyableCommand label="macOS / Linux" command={AGENT_ADD_MCP_COMMANDS.macosLinux} />
            <CopyableCommand label="Windows (PowerShell)" command={AGENT_ADD_MCP_COMMANDS.windows} />
            <hr className="my-2" />
            <div>
              If that says the command is not found, the plugin isn&apos;t installed yet.{' '}
              <Button
                variant="link"
                size="sm"
                className="p-0 align-baseline"
                onClick={() => setShowInstall((shown) => !shown)}
              >
                {showInstall ? 'Hide install commands' : 'Show install commands'}
              </Button>
            </div>
            {showInstall && (
              <div className="mt-2">
                <CopyableCommand label="macOS / Linux, in a terminal" command={AGENT_INSTALL_COMMANDS.macosLinux} />
                <CopyableCommand label="Windows, in PowerShell" command={AGENT_INSTALL_COMMANDS.windows} />
              </div>
            )}
          </div>
        )}
      </Alert>

      <h5>Your keys</h5>
      {revokeError && (
        <Alert variant="danger" className="py-2">
          {revokeError}
        </Alert>
      )}
      {keys.isLoading && <Spinner animation="border" size="sm" />}
      {keys.isError && <Alert variant="danger">{errorMessage(keys.error, 'Unable to load your keys.')}</Alert>}
      {keys.data && keys.data.length === 0 && <p className="text-muted">You have no keys.</p>}
      {keys.data && keys.data.length > 0 && (
        <Table size="sm" responsive>
          <thead>
            <tr>
              <th>Device</th>
              <th>Key ID</th>
              <th>Created</th>
              <th>Last used</th>
              <th>
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {keys.data.map((key) => (
              <tr key={key.keyId}>
                <td>{deviceName(key.label)}</td>
                <td>
                  <code>{key.keyId}</code>
                </td>
                <td>{formatWhen(key.issuedAt)}</td>
                <td>{formatWhen(key.lastUsedAt)}</td>
                <td className="text-end">
                  {confirming === key.keyId ? (
                    <>
                      <Button
                        variant="danger"
                        size="sm"
                        className="me-2"
                        disabled={revoking !== null}
                        onClick={() => void revoke(key.keyId)}
                      >
                        Confirm revoke
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={() => setConfirming(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline-danger" size="sm" onClick={() => setConfirming(key.keyId)}>
                      Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default McpAccessContent;
